import { FunctionComponent, useState } from "react";

import { Box, Button, Grid, GridItem, Text } from "@chakra-ui/react";
import { Global } from "@emotion/react";
import { IconChevronDown, IconExternalLink } from "@tabler/icons-react";
import { flushSync } from "react-dom";
import sanitizeHtml from "sanitize-html";

import { useCurrentUser } from "../../api";
/*
 * Background: Photo by Milad Fakurian on Unsplash
 * https://unsplash.com/photos/blue-orange-and-yellow-wallpaper-E8Ufcyxz514
 */
import bannerBg from "../../assets/images/global-engage-banner-bg.jpg";

const EXPANDED_GRID = {
    templateAreas: `"title collapse-btn" "description description" "primary-cta secondary-cta"`,
    templateColumns: "1fr auto",
} as const;

const COLLAPSED_GRID = {
    templateAreas: `"title primary-cta collapse-btn"`,
    templateColumns: "1fr auto auto",
} as const;

const ALLOWED_TAGS = ["a", "b", "br", "em", "i", "li", "ol", "p", "span", "strong", "u", "ul"];
const ALLOWED_ATTRIBUTES = {
    a: ["href", "target", "rel"],
};

const sanitizeBannerHtml = (html?: string) =>
    sanitizeHtml(html ?? "", {
        allowedTags: ALLOWED_TAGS,
        allowedAttributes: ALLOWED_ATTRIBUTES,
    });

type DocumentWithVT = Document & { startViewTransition?: (cb: () => void) => void };

interface Props {
    defaultExpanded?: boolean;
}
export interface GlobalEngageBannerProps {
    title: string;
    infoLinkText: string;
    description?: string;
    note?: string;
    engageLinkText?: string;
    engageLink?: string;
    infoLink?: string;
    supportingButtonText?: string;
    supportingButtonLink?: string;
}

export const GlobalEngageBanner: FunctionComponent<Props & GlobalEngageBannerProps> = ({ defaultExpanded = true, ...bannerProps }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const currentUserQuery = useCurrentUser();

    if (!currentUserQuery.data?.isInternal) return null;

    const toggle = (next: boolean) => {
        const vt = (document as DocumentWithVT).startViewTransition;
        if (vt) {
            vt.call(document, () => flushSync(() => setExpanded(next)));
        } else {
            setExpanded(next);
        }
    };

    return (
        <>
            {/* Suppress the default root cross-fade so only named elements animate */}
            <Global
                styles={`
                ::view-transition-old(root),
                ::view-transition-new(root) { animation: none; }
            `}
            />
            <Box
                mb="1.5rem"
                borderRadius=".5rem"
                overflow="hidden"
                position="relative"
                willChange="height"
                style={{ viewTransitionName: "global-engage-banner" }}
                backgroundImage={`linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.4) 15%, rgba(0,0,0,0.2) 50%, transparent 85%, transparent 100%), url(${bannerBg})`}
                backgroundSize="100% 100%, cover"
                backgroundPosition="0 0, top center"
                p="1rem 2rem 1rem"
            >
                <Grid {...(expanded ? EXPANDED_GRID : COLLAPSED_GRID)} rowGap="1.25rem" columnGap="0.75rem" alignContent="center">
                    <GridItem area="title" display="flex" alignItems="center">
                        <Text
                            color="white"
                            fontWeight="bold"
                            fontSize={expanded ? "2rem" : "1.5rem"}
                            textShadow={expanded ? undefined : "0 0 3px rgba(0,0,0,0.5)"}
                            style={{ viewTransitionName: "global-engage-banner-title" }}
                            dangerouslySetInnerHTML={{ __html: sanitizeBannerHtml(bannerProps.title) }}
                        />
                    </GridItem>

                    <GridItem area="collapse-btn" display="flex" alignItems={expanded ? "flex-start" : "center"} justifyContent="flex-end">
                        <Button
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: "whiteAlpha.200" }}
                            rightIcon={
                                <IconChevronDown
                                    style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", viewTransitionName: "global-engage-banner-toggle-svg" }}
                                    size={16}
                                />
                            }
                            onClick={() => toggle(!expanded)}
                            style={{ viewTransitionName: "global-engage-banner-toggle" }}
                            sx={expanded ? { "& p": { display: "none" }, "& span": { marginLeft: 0 } } : undefined}
                        >
                            {!expanded && <Text style={{ viewTransitionName: "global-engage-banner-toggle-text" }}>Learn More</Text>}
                        </Button>
                    </GridItem>

                    {expanded && (
                        <GridItem area="description">
                            <Text
                                color="whiteAlpha.900"
                                mb="0.5rem"
                                maxW="200ch"
                                sx={{
                                    textWrap: "balance",
                                    a: { color: "white", fontWeight: "bold", textDecoration: "underline" },
                                    "p:last-child": { marginBottom: 0 },
                                }}
                                dangerouslySetInnerHTML={{ __html: sanitizeBannerHtml(bannerProps.description) }}
                            />
                            <Text
                                color="white"
                                maxW="200ch"
                                sx={{
                                    textWrap: "balance",
                                    a: { color: "white", fontWeight: "bold", textDecoration: "underline" },
                                    "p:last-child": { marginBottom: 0 },
                                }}
                                dangerouslySetInnerHTML={{ __html: sanitizeBannerHtml(bannerProps.note) }}
                            />
                        </GridItem>
                    )}

                    <GridItem area="primary-cta" display="flex" alignItems={expanded ? "flex-end" : "center"}>
                        <Button
                            as="a"
                            href={bannerProps.engageLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="solid"
                            bg="white"
                            color={expanded ? "purple.600" : "purple.600"}
                            _hover={{ bg: "gray.100", textDecoration: "none" }}
                        >
                            {bannerProps.engageLinkText}
                        </Button>
                    </GridItem>

                    {expanded && (
                        <GridItem area="secondary-cta" display="flex" alignItems="flex-end" justifyContent="flex-end" gap="0.5rem">
                            <Button
                                as="a"
                                href={bannerProps.infoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="sm"
                                variant="ghost"
                                color="white"
                                _hover={{ bg: "whiteAlpha.200", textDecoration: "none" }}
                                rightIcon={<IconExternalLink size={14} />}
                            >
                                <span dangerouslySetInnerHTML={{ __html: sanitizeBannerHtml(bannerProps.infoLinkText) }} />
                            </Button>
                            {bannerProps.supportingButtonText && bannerProps.supportingButtonLink && (
                                <Button
                                    as="a"
                                    href={bannerProps.supportingButtonLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    variant="solid"
                                    bg="white"
                                    color="blackAlpha.700"
                                    _hover={{ bg: "gray.100", textDecoration: "none" }}
                                >
                                    {bannerProps.supportingButtonText}
                                </Button>
                            )}
                        </GridItem>
                    )}
                </Grid>
            </Box>
        </>
    );
};
